import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import {
    OverlayService,
    TelXLActivityHistoryPanelComponent,
} from '@dxp/components';
import { TelXLButtonComponent, TelXLTabComponent } from '@dxp/elements';
import { ICustomerDetails, ITab } from '@dxp/core/models';
import { PermissionService } from '@dxp/core/services';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { filter, Observable, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EndCustomerDetailsFormComponent } from '../customer-details-form/customer-details-form.component';
import { MergeCustomerModalComponent } from '../dialogs/merge-customer/merge-customer.component';
import { SwapCustomerModalComponent } from '../dialogs/swap-customer/swap-customer.component';
import { EndCustomerApiService } from '../services/end-customer.api.service';
import { EndCustomerStateService } from '../services/end-customer.state.service';
import { EndCustomerDetailsInitialsComponent } from './customer-details-initials/customer-details-initials.component';
import { CustomerDetailsService, FormAction } from './customer-details.service';

type Options = 'profile' | 'history';

@Component({
    selector: 'end-customer-details',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        EndCustomerDetailsInitialsComponent,
        EndCustomerDetailsFormComponent,
        TelXLActivityHistoryPanelComponent,
        TelXLButtonComponent,
        TelXLTabComponent,
        NgbTooltip,
    ],
    templateUrl: './customer-details.component.html',
    styleUrl: './customer-details.component.scss',
})
export class EndCustomerDetailsComponent implements OnInit {
    readonly #destroyRef = inject(DestroyRef);
    readonly #customerDetailsService = inject(CustomerDetailsService);
    readonly #endCustomerStateService = inject(EndCustomerStateService);
    readonly #endCustomerApiService = inject(EndCustomerApiService);
    readonly #permissionService = inject(PermissionService);
    readonly #overlayService = inject(OverlayService);

    allowNavigation = input.required<boolean>();
    updated = output<ICustomerDetails>();

    canSave$: Observable<boolean>;
    readonly #selected = signal<Options>('profile');
    readonly isEditing = signal<boolean>(false);

    readonly selectedEndCustomer = computed<ICustomerDetails | undefined>(() =>
        this.#endCustomerStateService.selectedEndCustomer(),
    );

    readonly isActiveCustomerSelected = computed<boolean>(() =>
        this.#endCustomerStateService.isActiveCustomerSelected(),
    );

    readonly hasActiveEndCustomer = computed<boolean>(
        () => this.#endCustomerStateService.activeEndCustomer() !== undefined,
    );

    readonly height = computed<string>(() =>
        this.#endCustomerStateService.isActiveCustomerSelected()
            ? 'calc(100vh - 525px)'
            : 'calc(100vh - 565px)',
    );

    readonly tabs = computed<ITab[]>(() => [
        { label: 'Profile', index: 'profile' },
        {
            label: 'History',
            index: 'history',
            disabled: this.selectedEndCustomer() === undefined,
        },
    ]);

    readonly canSwap = computed<boolean>(() => {
        const conversation = this.#endCustomerStateService.conversation();
        const mediaType = this.#endCustomerStateService.mediaType();
        const email = this.selectedEndCustomer()?.email ?? '';

        if (!conversation) return false;

        if (mediaType === 'Email') {
            return email.trim().length > 0;
        }

        return true;
    });

    constructor() {
        this.canSave$ = this.#customerDetailsService.canSave$;
    }

    ngOnInit() {
        if (
            this.#endCustomerStateService.selectedEndCustomer() === undefined &&
            this.hasCanCreatePermission
        ) {
            this.#customerDetailsService.updateEditing(true);
        }

        this.#customerDetailsService.isEditing$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(isEditing => {
                this.isEditing.set(isEditing);
            });
    }

    performFormAction(action: FormAction) {
        this.#customerDetailsService.performFormAction(action);
    }

    performEdit() {
        this.#customerDetailsService.updateEditing(true);
    }

    viewDirectory() {
        this.#customerDetailsService.updateEditing(false);
        this.#endCustomerStateService.setView('directory');
    }

    makeActiveCustomer() {
        const selectedCustomer =
            this.#endCustomerStateService.selectedEndCustomer();
        const conversationId = this.#endCustomerStateService.conversation();

        if (!selectedCustomer || !conversationId) return;

        this.#overlayService
            .open$(SwapCustomerModalComponent, {
                inputs: {
                    swapTo: selectedCustomer.name,
                },
            })
            .pipe(
                filter(Boolean),
                switchMap(() =>
                    this.#endCustomerApiService.makeCustomerActive(
                        selectedCustomer,
                        conversationId,
                    ),
                ),
                switchMap(() =>
                    this.#endCustomerStateService.setActiveEndCustomer(
                        selectedCustomer.customerId,
                    ),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    mergeCustomers() {
        const selectedCustomer =
            this.#endCustomerStateService.selectedEndCustomer();
        const conversationId = this.#endCustomerStateService.conversation();
        const activeEndCustomer =
            this.#endCustomerStateService.activeEndCustomer();

        if (!selectedCustomer || !conversationId || !activeEndCustomer) return;

        this.#overlayService
            .open$(MergeCustomerModalComponent, {
                inputs: { mergeWith: selectedCustomer.name },
            })
            .pipe(
                filter(Boolean),
                switchMap(() =>
                    this.#endCustomerApiService.mergeCustomers(
                        selectedCustomer,
                        activeEndCustomer,
                    ),
                ),
                switchMap(() =>
                    this.#endCustomerStateService.setActiveEndCustomer(
                        activeEndCustomer.customerId,
                    ),
                ),
                tap(customer =>
                    this.#endCustomerStateService.setSelectedEndCustomer(
                        customer,
                    ),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe();
    }

    onUpdated(customer: ICustomerDetails) {
        this.updated.emit(customer);
    }

    get selected() {
        return this.#selected();
    }

    set selected(value: string) {
        this.#selected.set(value as Options);
    }

    setSelected(panel: string) {
        this.selected = panel;
    }

    get hasCanCreatePermission(): boolean {
        return this.#permissionService.hasAnyPermission(['customer.create']);
    }

    get hasCanUpdatePermission(): boolean {
        return this.#permissionService.hasAnyPermission(['customer.update']);
    }

    get customer(): ICustomerDetails {
        return this.selectedEndCustomer() as ICustomerDetails;
    }
}
